/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from '@mui/material';
import type { DialogProps, PaperProps } from '@mui/material';
import React from 'react';
import { Resizable } from '../DraggableResizable';
import { CustomDialogHeader } from './CustomDialogHeader';
import type { Size } from '../DraggableResizable';
import type { HeaderAction } from './CustomDialogHeader';
import type { DialogAction } from './types';

export interface CustomDialogProps extends DialogProps {
  description?: string;
  actions?: React.ReactNode;
  onClose: (reason: DialogAction, muiReason?: string) => void;
  isResizable?: boolean;
  resizableDefaultSize?: Size;
  hasLayout?: boolean;
  shouldRenderInline?: boolean;
  headerActions?: HeaderAction[];
  minWidth?: number | string;
}

const PaperComponent: React.FC<
  PaperProps & {
    defaultSize?: Size;
    onResizeStop?: (size: Size) => void;
    hasLayout?: boolean;
    minWidth?: number | string;
  }
> = ({
  defaultSize = { width: 'auto', height: undefined },
  onResizeStop: onResize,
  hasLayout = true,
  minWidth = 280,
  ...props
}) => {
  const maxWidth = '98%';
  const maxHeight = '98%';

  return (
    <Resizable
      className="custom-dialog-resize"
      maxHeight={maxHeight}
      maxWidth={maxWidth}
      minHeight={120}
      minWidth={minWidth}
      size={defaultSize}
      style={{
        display: 'flex',
      }}
      {...(onResize && {
        onResizeStop: (_event, _direction, elementRef: HTMLElement): void => {
          const { width, height } = elementRef.getBoundingClientRect();
          onResize({ width, height });
        },
      })}
      enable={{
        top: true,
        right: true,
        bottom: true,
        left: true,
        topRight: true,
        bottomRight: true,
        bottomLeft: true,
        topLeft: true,
      }}
      resizeRatio={[2, 2]}
    >
      {hasLayout ? (
        <Paper
          {...props}
          sx={{
            margin: '0px!important',
            width: '100%!important',
            maxHeight: '100%!important',
            maxWidth: '100%!important',
          }}
        />
      ) : (
        props.children
      )}
    </Resizable>
  );
};

export const CustomDialog: React.FC<CustomDialogProps> = ({
  title,
  onClose,
  description = '',
  actions = null,
  children,
  sx,
  resizableDefaultSize,
  isResizable = false,
  hasLayout = true,
  shouldRenderInline = false,
  slotProps,
  disableRestoreFocus = true,
  headerActions,
  minWidth,
  ...other
}: CustomDialogProps) => {
  const [size, setSize] = React.useState<Size | undefined>(
    resizableDefaultSize,
  );

  return (
    <Dialog
      aria-labelledby="dialog-title"
      data-testid="customDialog"
      // fix for briefly showing default options when closing dialog
      transitionDuration={0}
      {...(isResizable && {
        PaperComponent,
      })}
      {...(shouldRenderInline && {
        disablePortal: true,
      })}
      disableRestoreFocus={disableRestoreFocus}
      onClose={(_event, reason): void => {
        onClose('CLOSED', reason);
      }}
      slotProps={{
        ...slotProps,
        paper: {
          sx: {
            ...(!other.fullWidth &&
              !other.maxWidth && {
                maxWidth: '560px',
              }),
            ...(other.maxWidth && {
              maxWidth: other.maxWidth,
            }),
            ...(Array.isArray(sx) ? Object.assign({}, ...sx) : sx),
          },
        },
        ...(isResizable && {
          paper: {
            defaultSize: size,
            onResizeStop: setSize,
            hasLayout,
            ...(minWidth && { minWidth }),
          } as never,
        }),
        ...(shouldRenderInline && {
          root: {
            style: {
              position: 'absolute',
            },
          },
          backdrop: {
            style: {
              position: 'absolute',
            },
          },
        }),
      }}
      sx={{
        ...(Array.isArray(sx) ? Object.assign({}, ...sx) : sx),
        ...(isResizable && {
          '.custom-dialog-resize': {
            height: 'initial',
          },
        }),
      }}
      {...other}
    >
      {hasLayout ? (
        <>
          <CustomDialogHeader
            headerActions={headerActions}
            onClose={(reason) => onClose('CLOSED', '')}
            title={title}
          />

          <DialogContent
            sx={{
              padding: '8px 16px',
            }}
          >
            {description ? (
              <Typography variant="body1">{description}</Typography>
            ) : null}
            {children}
          </DialogContent>
          {!headerActions && (
            <DialogActions
              sx={{
                padding: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              {actions}
            </DialogActions>
          )}
        </>
      ) : (
        children
      )}
    </Dialog>
  );
};

export default CustomDialog;
